import React from 'react';

import styled, { useTheme } from 'styled-components';

import { AnimatePresence } from 'framer-motion';

import moment from 'moment';
import { Translate } from 'react-localize-redux';

import { MerchProduct } from '@services/merch/types/api/merch-product';
import { getMerchProductMask } from '@webapp/utils/merch/getMerchProductMask';

import SimpleArtist from '@common/types/artist/simple-artist';

import Flex from '@components/layout/flex';
import Grid from '@components/basics/grid';
import Pill from '@components/basics/pill';
import DotsIcon from '@components/basics/icons/dots';
import SquareImg from '@components/basics/artwork-img';
import Typography from '@components/basics/typography';
import StatusLabelMerch from '@components/status-label-merch';
import FixedBottomMenu from '@components/layout/fixed-bottom-menu';
import CircleArrowIcon from '@components/basics/icons/circle-arrow';
import MerchColorChoice from '@components/merch-row/merch-color-choice';
import { getMerchMenuOptions } from '@components/merch-row/menu-options';
import { PublishingStatus } from '@components/merch-row/desktop-view/info-row';
import { SimpleRelease } from '@common/types/release/simple-release';

declare type MobileInfoRowProps = {
  className?: string;
  user: any;
  allReleases: SimpleRelease[];
  item: MerchProduct;
  artist: SimpleArtist;
  isListOpen: boolean;
  openList: () => void;
  baseUrl: string;
  isFilteredByAddedDate: boolean;
  handleCatalogMenu: (
    action: string,
    release: MerchProduct,
    baseUrl: string,
    artist: SimpleArtist,
    user: any,
    allReleases: SimpleRelease[],
    handleShowNotification: () => void
  ) => void;
  handleShowNotification: () => void;
};

export default function MobileInfoRow({
  className,
  user,
  allReleases,
  item,
  artist,
  isListOpen,
  openList,
  baseUrl,
  isFilteredByAddedDate,
  handleShowNotification,
  handleCatalogMenu,
}: MobileInfoRowProps) {
  const theme = useTheme();
  const menuOptions = getMerchMenuOptions(item, artist);
  const maskProduct = getMerchProductMask(item);
  const [dropdown, setDropdown] = React.useState<boolean>(false);

  // TODO: DEVTEAM-603 - const timeLeft - This needs to be integrated
  const [timeLeft]: any = React.useState({
    days: 0,
    hours: 1,
    minutes: 26,
    seconds: 42,
    width: 50,
  });
  const inMinutes =
    timeLeft.days * 24 * 60 + timeLeft.hours * 60 + timeLeft.minutes;

  return (
    <Row className={`${className || ''} ${isListOpen ? 'menu-opened' : ''}`}>
      <Flex justifyContent="space-between" alignItems="center" gap={20}>
        <Flex alignItems="center" gap={20}>
          {!!maskProduct && (
            <SquareImg size={36} srcImg={maskProduct} altImg={item.title} />
          )}
          <Grid gap={5}>
            <Typography className="merch-name" customFont="semi-bold">
              {item.title}
            </Typography>
            <MerchColorChoice colors={item.colors} />
          </Grid>
        </Flex>
        {/* TODO: DEVTEAM-603 - Implement NFT for each merch. */}
        {item.isNFT && <Pill>NFT</Pill>}
      </Flex>

      {isFilteredByAddedDate ? (
        <Flex justifyContent="space-between" alignItems="center" gap={20}>
          <Typography weight="700">
            <Translate id="CATALOG.HEADER.DATE_ADDED" />
          </Typography>
          <Typography color={theme.colors.tertiary}>
            {moment(item.createdAt).format('DD MMM YY')}
          </Typography>
        </Flex>
      ) : null}

      <Flex justifyContent="space-between" alignItems="center" gap={20}>
        <Typography weight="700">
          <Translate id="CATALOG.HEADER.PRICE_PROFIT" />
        </Typography>
        <Typography color={theme.colors.tertiary}>
          ${item.price?.toFixed(2)}/$
          {Math.round(item.price - item.cost)?.toFixed(2)}
        </Typography>
      </Flex>

      <Flex justifyContent="space-between" alignItems="center" gap={10}>
        {item.status !== 'publishing' ? (
          <StatusLabelMerch
            status={item.status}
            approved={!!item.status}
            releaseDate={item.releaseAt}
          />
        ) : (
          <PublishingStatus progressWidth={100 - timeLeft?.width ?? 0}>
            <Typography type="small" weight="700" className="publishing-text">
              <Translate
                id="CATALOG.STATUS.PUBLISHING_IN"
                data={{
                  time: `${inMinutes >= 10 ? inMinutes : '0' + inMinutes}:${
                    timeLeft.seconds >= 10
                      ? timeLeft.seconds
                      : '0' + timeLeft.seconds
                  }`,
                }}
              />
            </Typography>
          </PublishingStatus>
        )}
        <Flex justifyContent="flex-end" alignItems="center" gap={10}>
          <StyledCircleArrowIcon
            isWhite
            smallArrow
            arrowUp={isListOpen}
            onClick={openList}
          />

          {menuOptions.length ? (
            <>
              <StyledDotsIcon onClick={() => setDropdown(true)} />
              <AnimatePresence exitBeforeEnter>
                {dropdown && (
                  <FixedBottomMenu
                    title={item.title}
                    options={menuOptions}
                    isShowing={dropdown}
                    onSelect={(action: string) =>
                      handleCatalogMenu(
                        action,
                        item,
                        baseUrl,
                        artist,
                        user,
                        allReleases,
                        handleShowNotification
                      )
                    }
                    onClose={() => setDropdown(false)}
                  />
                )}
              </AnimatePresence>
            </>
          ) : null}
        </Flex>
      </Flex>
    </Row>
  );
}

const Row = styled.div`
  border-radius: 10px;
  background-color: ${({ theme }) => theme.colors.box};
  width: 100%;
  display: grid;
  grid-gap: 10px;
  padding: 20px;

  &.menu-opened {
    border-radius: 10px 10px 0 0;
  }
`;

const StyledDotsIcon = styled(DotsIcon)`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
`;

const StyledCircleArrowIcon = styled(CircleArrowIcon)`
  width: 32px;
  height: 32px;
`;
